import { useState, useEffect, useCallback, useMemo } from 'react'
import { Modal, Switch, App, Spin, Checkbox } from 'antd'
import { Copy, Check, Globe, Lock, Filter } from 'lucide-react'
import styled from 'styled-components'
import { MindmapService } from '../../../services/mindmap/mindmapService'
import { useOptionalLabelFilterContext } from '../../../features/labelFilter'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.lg};
`

const ShareToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.md};
  background: var(--color-bg-card);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`

const ToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
`

const ToggleIcon = styled.div<{ $isPublic: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(props) => (props.$isPublic ? 'var(--color-success-bg)' : 'var(--color-bg-hover)')};
  color: ${(props) => (props.$isPublic ? 'var(--color-success)' : 'var(--color-text-secondary)')};
`

const ToggleText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ToggleTitle = styled.span`
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

const ToggleDescription = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

const UrlSection = styled.div<{ $visible: boolean }>`
  display: ${(props) => (props.$visible ? 'flex' : 'none')};
  flex-direction: column;
  gap: ${SPACING.sm};
`

const UrlLabel = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-secondary);
`

const UrlInputRow = styled.div`
  display: flex;
  gap: ${SPACING.sm};
`

const UrlInput = styled.input`
  flex: 1;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid var(--color-border-dark);
  border-radius: 8px;
  background: var(--color-bg-elevated);
  color: var(--color-text-primary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`

const CopyButton = styled.button<{ $copied: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: ${SPACING.sm} ${SPACING.md};
  border: 1px solid
    ${(props) => (props.$copied ? 'var(--color-success)' : 'var(--color-border-dark)')};
  border-radius: 8px;
  background: ${(props) =>
    props.$copied ? 'var(--color-success-bg)' : 'var(--color-bg-elevated)'};
  color: ${(props) => (props.$copied ? 'var(--color-success)' : 'var(--color-text-primary)')};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.$copied ? 'var(--color-success-bg)' : 'var(--color-bg-hover)')};
  }
`

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.xl};
`

const FilterOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} ${SPACING.md};
  background: var(--color-bg-hover);
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: var(--color-bg-elevated);
  }
`

const FilterOptionLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

const FilterCount = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-primary);
  font-weight: ${FONT_WEIGHT.medium};
`

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  mindmapId: string
}

export function ShareModal({ isOpen, onClose, mindmapId }: ShareModalProps) {
  const { notification } = App.useApp()
  const [isPublic, setIsPublic] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isUpdating, setIsUpdating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [includeFilters, setIncludeFilters] = useState(false)

  // Get current filter state (may be undefined if outside filter context)
  const filterContext = useOptionalLabelFilterContext()
  const selectedFilterLabels = useMemo(
    () => filterContext?.selectedFilterLabels ?? [],
    [filterContext?.selectedFilterLabels]
  )
  const hasActiveFilters = selectedFilterLabels.length > 0

  // Build share URL with optional filter params
  const shareUrl = useMemo(() => {
    const baseUrl = `${window.location.origin}/app/view/${mindmapId}`
    if (includeFilters && hasActiveFilters) {
      return `${baseUrl}?filter=${selectedFilterLabels.join(',')}`
    }
    return baseUrl
  }, [mindmapId, includeFilters, hasActiveFilters, selectedFilterLabels])

  // Fetch current is_public state when modal opens
  useEffect(() => {
    if (isOpen && mindmapId) {
      setIsLoading(true)
      void MindmapService.get(mindmapId).then(({ data, error }) => {
        if (data && !error) {
          setIsPublic(data.is_public)
        }
        setIsLoading(false)
      })
    }
  }, [isOpen, mindmapId])

  const handleToggle = useCallback(
    async (checked: boolean) => {
      setIsUpdating(true)
      const { error } = await MindmapService.update(mindmapId, { is_public: checked })

      if (error) {
        notification.error({
          title: 'Failed to update sharing settings',
          description: error.message,
        })
      } else {
        setIsPublic(checked)
        notification.success({
          title: checked ? 'Public sharing enabled' : 'Public sharing disabled',
        })
      }
      setIsUpdating(false)
    },
    [mindmapId, notification]
  )

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [shareUrl])

  return (
    <Modal
      title="Share Project"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnHidden
    >
      {isLoading ? (
        <LoadingOverlay>
          <Spin />
        </LoadingOverlay>
      ) : (
        <ModalContent>
          <ShareToggleRow>
            <ToggleLabel>
              <ToggleIcon $isPublic={isPublic}>
                {isPublic ? <Globe size={18} /> : <Lock size={18} />}
              </ToggleIcon>
              <ToggleText>
                <ToggleTitle>Public access</ToggleTitle>
                <ToggleDescription>
                  {isPublic ? 'Anyone with the link can view' : 'Only you can access this project'}
                </ToggleDescription>
              </ToggleText>
            </ToggleLabel>
            <Switch checked={isPublic} onChange={handleToggle} loading={isUpdating} />
          </ShareToggleRow>

          <UrlSection $visible={isPublic}>
            <UrlLabel>Shareable link</UrlLabel>
            {hasActiveFilters && (
              <FilterOptionRow onClick={() => setIncludeFilters(!includeFilters)}>
                <Checkbox checked={includeFilters} />
                <FilterOptionLabel>
                  <Filter size={14} />
                  Include applied filters
                  <FilterCount>({selectedFilterLabels.length})</FilterCount>
                </FilterOptionLabel>
              </FilterOptionRow>
            )}
            <UrlInputRow>
              <UrlInput
                type="text"
                value={shareUrl}
                readOnly
                onClick={(e) => e.currentTarget.select()}
              />
              <CopyButton $copied={copied} onClick={handleCopy}>
                {copied ? <Check size={16} /> : <Copy size={16} />}
                {copied ? 'Copied' : 'Copy'}
              </CopyButton>
            </UrlInputRow>
          </UrlSection>
        </ModalContent>
      )}
    </Modal>
  )
}
